import type { MouseEvent, ReactNode } from "react";
import { Box, ButtonBase, Typography, useTheme } from "@mui/material";
import { ExamIcon } from "../../icons/ExamIcon";
import { Column } from "../../misc/Column";
import { Row } from "../../misc/Row";
import { renderAddress } from "../../misc/AddressDisplay";
import { isTheoryExam } from "../../model/autovioCalendarEvents";
import type { Exam } from "../../utils/examFilters";

export function ExamMobileListItem(props: {
  branchNames?: string;
  exam: Exam;
  instructorName?: string;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  status: ReactNode;
  studentName?: string;
}) {
  const { branchNames, exam, instructorName, onClick, status, studentName } = props;
  const theme = useTheme();
  const examType = isTheoryExam(exam) ? "Theoretische Prüfung" : "Praktische Prüfung";
  const dateAndTime = `${exam.start.toFormat("dd.MM.yyyy, HH:mm")}–${exam.end.toFormat("HH:mm")}`;
  const details = [instructorName, branchNames, renderAddress(exam.startLocation, { oneLine: true })].filter(Boolean);

  return (
    <ButtonBase
      data-testid="ExamMobileListItem"
      onClick={onClick}
      sx={{
        alignItems: "stretch",
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxSizing: "border-box",
        display: "block",
        px: 0,
        py: 1,
        textAlign: "left",
        width: "100%",
      }}
    >
      <Column sx={{ gap: 0.5, minWidth: 0 }}>
        <Row sx={{ alignItems: "flex-start", gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              alignItems: "center",
              backgroundColor: theme.palette.action.hover,
              borderRadius: "50%",
              color: theme.palette.text.secondary,
              display: "flex",
              flexShrink: 0,
              height: 40,
              justifyContent: "center",
              width: 40,
            }}
          >
            <ExamIcon />
          </Box>
          <Column sx={{ gap: 0.25, minWidth: 0 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
              {studentName ?? "Unbekannter Fahrschüler"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {examType} · {dateAndTime}
            </Typography>
          </Column>
        </Row>
        <Row sx={{ alignItems: "flex-end", justifyContent: "space-between", gap: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
            {details.join(" · ")}
          </Typography>
          <Box sx={{ flexShrink: 0 }}>{status}</Box>
        </Row>
      </Column>
    </ButtonBase>
  );
}
